import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { Input, Select, Textarea } from '../../components';
import StepContainer from '../../components/onboarding/StepContainer';
import TokenCostModal from '../../components/TokenCostModal';
import { useOnboardingStore } from '../../stores/onboardingStore';
import apiClient from '../../lib/api';

// Estimated token cost for brand extraction (GPT-4 Vision + processing)
const BRAND_EXTRACTION_TOKEN_COST = 3500;

const companyInfoSchema = z.object({
  companyName: z.string().min(2, 'Company name must be at least 2 characters'),
  website: z.string().url('Please enter a valid URL').min(1, 'Website URL is required'),
  industry: z.string().min(1, 'Please select an industry'),
  description: z
    .string()
    .optional()
    .refine(
      (val) => !val || val.trim().length >= 20,
      'Description must be at least 20 characters'
    )
    .refine(
      (val) => !val || val.trim().length <= 600,
      'Description must be less than 600 characters'
    ),
  country: z.string().min(2, 'Please select a country'),
  phone: z.string().optional(),
});

type CompanyInfoForm = z.infer<typeof companyInfoSchema>;

const INDUSTRIES = [
  { value: '', label: 'Select your industry' },
  { value: 'technology', label: 'Technology & Software' },
  { value: 'ecommerce', label: 'E-commerce & Retail' },
  { value: 'marketing', label: 'Marketing & Advertising' },
  { value: 'healthcare', label: 'Healthcare & Medical' },
  { value: 'finance', label: 'Finance & Banking' },
  { value: 'education', label: 'Education & Training' },
  { value: 'realestate', label: 'Real Estate' },
  { value: 'hospitality', label: 'Hospitality & Travel' },
  { value: 'manufacturing', label: 'Manufacturing & Industrial' },
  { value: 'consulting', label: 'Consulting & Services' },
  { value: 'other', label: 'Other' },
];

const COUNTRIES = [
  { value: '', label: 'Select country' },
  { value: 'NL', label: '🇳🇱 Netherlands' },
  { value: 'BE', label: '🇧🇪 Belgium' },
  { value: 'DE', label: '🇩🇪 Germany' },
  { value: 'FR', label: '🇫🇷 France' },
  { value: 'GB', label: '🇬🇧 United Kingdom' },
  { value: 'US', label: '🇺🇸 United States' },
  { value: 'ES', label: '🇪🇸 Spain' },
  { value: 'IT', label: '🇮🇹 Italy' },
  { value: 'AT', label: '🇦🇹 Austria' },
  { value: 'CH', label: '🇨🇭 Switzerland' },
  { value: 'PL', label: '🇵🇱 Poland' },
  { value: 'SE', label: '🇸🇪 Sweden' },
  { value: 'DK', label: '🇩🇰 Denmark' },
  { value: 'NO', label: '🇳🇴 Norway' },
  { value: 'FI', label: '🇫🇮 Finland' },
  { value: 'IE', label: '🇮🇪 Ireland' },
  { value: 'PT', label: '🇵🇹 Portugal' },
  { value: 'GR', label: '🇬🇷 Greece' },
  { value: 'CZ', label: '🇨🇿 Czech Republic' },
  { value: 'HU', label: '🇭🇺 Hungary' },
  { value: 'RO', label: '🇷🇴 Romania' },
  { value: 'BG', label: '🇧🇬 Bulgaria' },
  { value: 'HR', label: '🇭🇷 Croatia' },
  { value: 'SK', label: '🇸🇰 Slovakia' },
  { value: 'SI', label: '🇸🇮 Slovenia' },
  { value: 'LU', label: '🇱🇺 Luxembourg' },
  { value: 'CA', label: '🇨🇦 Canada' },
  { value: 'AU', label: '🇦🇺 Australia' },
  { value: 'NZ', label: '🇳🇿 New Zealand' },
  { value: 'JP', label: '🇯🇵 Japan' },
  { value: 'KR', label: '🇰🇷 South Korea' },
  { value: 'SG', label: '🇸🇬 Singapore' },
  { value: 'AE', label: '🇦🇪 United Arab Emirates' },
  { value: 'ZA', label: '🇿🇦 South Africa' },
  { value: 'BR', label: '🇧🇷 Brazil' },
  { value: 'MX', label: '🇲🇽 Mexico' },
  { value: 'AR', label: '🇦🇷 Argentina' },
  { value: 'CL', label: '🇨🇱 Chile' },
  { value: 'IN', label: '🇮🇳 India' },
  { value: 'CN', label: '🇨🇳 China' },
];

interface WebsiteValidationState {
  isValidating: boolean;
  isValid: boolean | null;
  isReachable: boolean | null;
  message: string | null;
}

export default function CompanyInfoStep() {
  const { t } = useTranslation();
  const { websiteData, workspaceData, saveWorkspaceData, nextStep, previousStep } = useOnboardingStore();

  const [websiteValidation, setWebsiteValidation] = useState<WebsiteValidationState>({
    isValidating: false,
    isValid: null,
    isReachable: null,
    message: null,
  });

  const [isGeneratingDescription, setIsGeneratingDescription] = useState(false);
  const [generateError, setGenerateError] = useState<string | null>(null);
  const [wordCount, setWordCount] = useState(0);
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CompanyInfoForm>({
    resolver: zodResolver(companyInfoSchema),
    defaultValues: {
      companyName: workspaceData.companyName || '',
      website: workspaceData.website || websiteData.domain || '', // Auto-fill from Step 2
      industry: workspaceData.industry || '',
      description: workspaceData.description || '',
      country: workspaceData.country || '',
      phone: workspaceData.phone || '',
    },
  });

  const websiteValue = watch('website');
  const descriptionValue = watch('description');

  // Update word count whenever description changes
  useEffect(() => {
    if (!descriptionValue || descriptionValue.trim() === '') {
      setWordCount(0);
    } else {
      const words = descriptionValue.trim().split(/\s+/).filter(word => word.length > 0);
      setWordCount(words.length);
    }
  }, [descriptionValue]);

  const validateWebsite = async (url: string) => {
    if (!url || url.trim() === '') {
      setWebsiteValidation({
        isValidating: false,
        isValid: null,
        isReachable: null,
        message: null,
      });
      return;
    }

    // Check if URL starts with http(s)
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      setWebsiteValidation({
        isValidating: false,
        isValid: false,
        isReachable: false,
        message: 'URL must start with http:// or https://',
      });
      return;
    }

    setWebsiteValidation({
      isValidating: true,
      isValid: null,
      isReachable: null,
      message: 'Checking website...',
    });

    try {
      const response = await apiClient.post('/utils/validate-url', { url });
      const { valid, reachable, error } = response.data;

      setWebsiteValidation({
        isValidating: false,
        isValid: valid,
        isReachable: reachable,
        message: reachable
          ? 'Website is reachable ✓'
          : error || 'Website could not be reached',
      });
    } catch (error: any) {
      setWebsiteValidation({
        isValidating: false,
        isValid: false,
        isReachable: false,
        message: 'Failed to validate website',
      });
    }
  };

  const handleWebsiteBlur = () => {
    if (websiteValue) {
      validateWebsite(websiteValue);
    }
  };

  const handleConfirmGenerate = async () => {
    if (!websiteValue || !websiteValue.trim()) {
      throw new Error('No website URL found');
    }

    setGenerateError(null);
    setIsGeneratingDescription(true);

    // First, try to get cached brand guide (fast path)
    try {
      const cacheResponse = await apiClient.get('/brand/guide/latest');
      if (cacheResponse.data?.companyDescription) {
        setValue('description', cacheResponse.data.companyDescription);
        setIsGeneratingDescription(false);
        return;
      }
    } catch (cacheError: any) {
      // 404 or no cached guide, continue to full extraction
      console.log('No cached brand guide found, performing full extraction');
    }

    // Fallback to full extraction if no cache exists (slow path)
    const response = await apiClient.post('/brand/extract-direct', {
      url: websiteValue,
    });

    if (response.data?.companyDescription) {
      setValue('description', response.data.companyDescription);
    } else {
      setGenerateError('Could not generate description. Please try again.');
    }

    setIsGeneratingDescription(false);
  };

  const onSubmit = async (data: CompanyInfoForm) => {
    // Save to store
    saveWorkspaceData(data);

    // TODO: In production, also PATCH to backend API
    // await apiClient.patch(`/workspace/${workspaceId}`, data);

    // Move to next step
    nextStep();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <StepContainer
        title={t('onboarding.companyInfo.title')}
        description={t('onboarding.companyInfo.subtitle')}
        onNext={handleSubmit(onSubmit)}
        onBack={previousStep}
        isLoading={isSubmitting || isGeneratingDescription}
      >
        <div className="space-y-6">
          <Input
            label={t('onboarding.companyInfo.companyName') + ' *'}
            placeholder="Acme Inc."
            error={errors.companyName?.message}
            {...register('companyName')}
          />

          <div className="relative">
            <Input
              label={t('onboarding.companyInfo.website')}
              type="url"
              placeholder="https://yourcompany.com"
              error={errors.website?.message}
              {...register('website')}
              onBlur={handleWebsiteBlur}
            />
            {websiteValidation.message && (
              <p
                className={`mt-2 text-sm ${
                  websiteValidation.isReachable
                    ? 'text-green-700 dark:text-green-300'
                    : websiteValidation.isValidating
                    ? 'text-gray-600 dark:text-gray-400'
                    : 'text-orange-700 dark:text-orange-300'
                }`}
              >
                {websiteValidation.isValidating && (
                  <span className="inline-block animate-spin mr-2">⏳</span>
                )}
                {websiteValidation.message}
              </p>
            )}
          </div>

          <Select
            label={t('onboarding.companyInfo.industry') + ' *'}
            options={INDUSTRIES}
            error={errors.industry?.message}
            {...register('industry')}
          />

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                {t('onboarding.companyInfo.description')}
              </label>
              <span
                className={`text-xs font-medium ${
                  wordCount === 0
                    ? 'text-gray-400 dark:text-gray-500'
                    : wordCount >= 20 && wordCount <= 100
                    ? 'text-green-600 dark:text-green-400'
                    : 'text-red-600 dark:text-red-400'
                }`}
              >
                {wordCount} / 100 words
              </span>
            </div>
            <Textarea
              placeholder={t('onboarding.companyInfo.descriptionPlaceholder')}
              rows={4}
              error={errors.description?.message}
              {...register('description')}
            />
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-gray-400">
                {t('onboarding.companyInfo.descriptionHelper')}
              </p>
              {websiteValue && !descriptionValue && (
                <button
                  type="button"
                  onClick={() => setShowGenerateModal(true)}
                  disabled={isGeneratingDescription || !websiteValidation.isReachable}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                >
                  {isGeneratingDescription ? (
                    <>Generating...</>
                  ) : (
                    <>
                      Generate from{' '}
                      {(() => {
                        try {
                          return new URL(websiteValue).hostname;
                        } catch {
                          return 'website';
                        }
                      })()}{' '}
                      ({BRAND_EXTRACTION_TOKEN_COST.toLocaleString()} tokens)
                    </>
                  )}
                </button>
              )}
            </div>
            {generateError && (
              <p className="text-xs text-red-600 dark:text-red-400 mt-1">{generateError}</p>
            )}
          </div>

          <Select
            label={t('onboarding.companyInfo.country') + ' *'}
            options={COUNTRIES}
            error={errors.country?.message}
            {...register('country')}
          />

          <Input
            label={t('onboarding.companyInfo.phone')}
            type="tel"
            placeholder="+31 6 12345678"
            error={errors.phone?.message}
            {...register('phone')}
          />
        </div>
      </StepContainer>

      {/* Token Cost Modal for Brand Extraction */}
      <TokenCostModal
        open={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onConfirm={handleConfirmGenerate}
        title={t('tokenCostModal.descriptionGeneration.title')}
        description={t('tokenCostModal.descriptionGeneration.description')}
        tokenCost={BRAND_EXTRACTION_TOKEN_COST}
        confirmButtonText={t('tokenCostModal.descriptionGeneration.confirm')}
        estimatedDuration={t('tokenCostModal.descriptionGeneration.duration')}
      />
    </form>
  );
}
